// The grid editor of a file's column dictionary (DataHarmonizer-style; task
// 2026-10-01-dataharmonizer-style-grid-editor): one row per column, cells for type, format,
// title, description, missing-value markers,
// required and unique. Validated as you type with the server's rules (./grid.js), and saved in
// ONE request: PATCH /api/files/{id}/columns with the changed rows (confirmed=true); a 422 puts
// each server message on its cell and nothing is saved.
//
// Built here rather than embedding DataHarmonizer: DataHarmonizer edits data rows against a
// LinkML template, is a whole app on Handsontable (whose licence is not free for every use),
// and brings its own look; the dictionary needs seven cells per column and the BOKU look. The
// decision is recorded in the task file.
//
// Keyboard: Tab / Shift+Tab move through the cells; Ctrl+arrow keys move in any direction from
// any cell; plain Up/Down move between rows, plain Left/Right leave a text cell at its first/last character, Enter in a text cell moves down.
// Paste: a block copied from a spreadsheet (TSV) fills cells from the focused cell to the right
// and down; pasted on a column name, the first pasted column names the rows; a first line of
// headers (Column, Type, Title, ...) maps the pasted columns by header.
//
// Props: { file, columns: [ColumnOut], onSaved(columns), onClose() }
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api } from "../../../api";
import { ErrorNote } from "../../../components/Feedback";
import { Badge, Btn, G_DARK, Input, Select, Toggle } from "../../../components/Shared";
import { rememberFocus } from "../../../focus";
import { applyPaste, draftOf, GRID_FIELDS, patchOf, serverCellErrors, validateDraft } from "./grid";
import { FIELD_TYPES } from "./terms";

const th = { padding: "8px 10px", textAlign: "left", fontSize: 12, color: "#6b7280", textTransform: "uppercase",
             letterSpacing: "0.05em", whiteSpace: "nowrap", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "6px 8px", verticalAlign: "top", borderTop: "1px solid #f3f4f6" };
const cellInput = { padding: "6px 8px", fontSize: 13 };
const WIDTH = { type: 130, format: 110, title: 150, description: 240, missing: 130 };
const errText = { display: "block", fontSize: 12, color: "#b91c1c", marginTop: 4, maxWidth: 260 };
const KEYS = ["column", ...GRID_FIELDS.map(f => f.key)];

const FOCUSABLE = "input, select, textarea, button, a[href], [tabindex]";

function cellEl(root, r, c) {
  const td = root && root.querySelector(`[data-cell="${r}:${c}"]`);
  if (!td) return null;
  return td.matches("[tabindex]") ? td : td.querySelector(FOCUSABLE);
}

function focusFirstInvalid(root) {
  const cell = root && root.querySelector("[data-invalid]");
  const el = cell && (cell.matches("[tabindex]") ? cell : cell.querySelector(FOCUSABLE));
  if (el) el.focus();
}

function caretAtEdge(el, dir) {
  if (!el || el.tagName !== "INPUT" || !["text", "search", ""].includes(el.type)) return true;
  try {
    const { selectionStart: a, selectionEnd: b, value } = el;
    if (a !== b) return false;
    return dir < 0 ? a === 0 : a === value.length;
  } catch {
    return true;
  }
}

export default function ColumnGrid({ file, columns, onSaved, onClose }) {
  const uid = useId();
  const tableRef = useRef(null);
  const [drafts, setDrafts] = useState(() => columns.map(draftOf));
  const [server, setServer] = useState({});
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tried, setTried] = useState(false);
  const [focusErrors, setFocusErrors] = useState(0);     // bumped: focus the first marked cell once rendered

  useEffect(() => { setDrafts(columns.map(draftOf)); setServer({}); }, [columns]);

  const patches = useMemo(() => columns.map((c, i) => patchOf(c, drafts[i])), [columns, drafts]);
  const invalid = useMemo(() => columns.map((c, i) => validateDraft(drafts[i], c)), [columns, drafts]);
  const changed = patches.filter(Boolean).length;
  const invalidCells = invalid.reduce((n, e, i) => n + (patches[i] ? Object.keys(e).length : 0), 0);

  const errorOf = (r, key) => {
    if (patches[r] && invalid[r][key]) return invalid[r][key];
    return (server[r] || {})[key] || null;
  };

  const setCell = (r, key, value) => {
    setDrafts(ds => ds.map((d, i) => (i === r ? { ...d, [key]: value } : d)));
    setServer(s => {
      if (!s[r] || !s[r][key]) return s;
      const row = { ...s[r] };
      delete row[key];
      return { ...s, [r]: row };
    });
  };

  const move = (r, c) => {
    const el = cellEl(tableRef.current, Math.max(0, Math.min(columns.length - 1, r)),
                      Math.max(0, Math.min(KEYS.length - 1, c)));
    if (el) el.focus();
  };

  const onKeyDown = e => {
    const cell = e.target.closest && e.target.closest("[data-cell]");
    if (!cell) return;
    const [r, c] = cell.dataset.cell.split(":").map(Number);
    const ctrl = e.ctrlKey || e.metaKey;
    const textInput = e.target.tagName === "INPUT";
    const go = (dr, dc) => { e.preventDefault(); move(r + dr, c + dc); };
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const d = e.key === "ArrowDown" ? 1 : -1;
      if (ctrl || !e.defaultPrevented) go(d, 0);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const d = e.key === "ArrowRight" ? 1 : -1;
      if (ctrl || (!e.defaultPrevented && caretAtEdge(e.target, d))) go(0, d);
    } else if (e.key === "Enter" && textInput && !e.defaultPrevented) {
      go(e.shiftKey ? -1 : 1, 0);
    }
  };

  const onPaste = e => {
    const text = e.clipboardData && e.clipboardData.getData("text/plain");
    // one value without a tab or line break: an ordinary paste into the cell
    if (!text || !/[\t\r\n]/.test(text.replace(/\r?\n$/, ""))) return;
    const cell = e.target.closest && e.target.closest("[data-cell]");
    if (!cell) return;
    e.preventDefault();
    const [r, c] = cell.dataset.cell.split(":").map(Number);
    const out = applyPaste(drafts, columns, r, KEYS[c], text);
    setDrafts(out.drafts);
    setServer(s => {
      const next = { ...s };
      out.touched.forEach(i => { delete next[i]; });
      return next;
    });
    setMessages([`Pasted into ${out.touched.size} row${out.touched.size === 1 ? "" : "s"}.`, ...out.messages]);
  };

  const discard = () => {
    setDrafts(columns.map(draftOf)); setServer({}); setMessages([]); setError(null); setTried(false);
  };

  useEffect(() => { if (focusErrors) focusFirstInvalid(tableRef.current); }, [focusErrors]);

  const save = async () => {
    setError(null); setMessages([]); setTried(true);
    if (!changed) { setMessages(["Nothing to save: no cell was changed."]); return; }
    if (invalidCells) {
      setMessages([`Fix the ${invalidCells} marked cell${invalidCells === 1 ? "" : "s"} first; nothing was saved.`]);
      setFocusErrors(n => n + 1);
      return;
    }
    const sent = [];
    const body = [];
    patches.forEach((p, i) => { if (p) { sent.push(i); body.push({ id: columns[i].id, ...p }); } });
    const restore = rememberFocus();
    setSaving(true);
    try {
      const out = await api.patch(`/files/${file.id}/columns`, body);
      setServer({}); setTried(false);
      onSaved(out, body.length);
    } catch (err) {
      if (err.status === 422 && Array.isArray(err.detail)) {
        const cells = serverCellErrors(err.detail, sent);
        setServer(cells);
        const n = Object.values(cells).reduce((k, row) => k + Object.keys(row).length, 0);
        setMessages([`The server refused ${n} cell${n === 1 ? "" : "s"}; nothing was saved. Correct the marked cells and save again.`]);
        setFocusErrors(k => k + 1);
      } else {
        setError(err);
      }
    } finally {
      setSaving(false);
      restore(() => tableRef.current && tableRef.current.querySelector("button"));
    }
  };

  const helpId = `${uid}-help`;
  const cellProps = (r, key, name) => {
    const err = errorOf(r, key);
    const errId = `${uid}-err-${r}-${key}`;
    return {
      err, errId,
      a11y: { "aria-label": `${GRID_FIELDS.find(f => f.key === key).label} of ${name}`,
              "aria-invalid": err ? "true" : undefined, "aria-describedby": err ? errId : undefined },
    };
  };

  const renderCell = (r, key, col) => {
    const d = drafts[r];
    const { err, errId, a11y } = cellProps(r, key, col.name);
    let control;
    if (key === "type") {
      const options = FIELD_TYPES.includes(d.type) ? FIELD_TYPES
        : [...FIELD_TYPES, { value: d.type, label: `${d.type} (not a type)` }];
      control = <Select {...a11y} value={d.type} options={options} onChange={e => setCell(r, "type", e.target.value)} />;
    } else if (key === "required" || key === "unique") {
      control = (
        <Toggle checked={d[key] === true} onChange={v => setCell(r, key, v)}
                label={`${col.name} is ${key}${err ? ` (${err})` : ""}`} />
      );
    } else {
      control = <Input {...a11y} value={d[key]} style={cellInput} onChange={e => setCell(r, key, e.target.value)} />;
    }
    return (
      <td key={key} data-cell={`${r}:${KEYS.indexOf(key)}`} data-invalid={err ? "" : undefined}
          style={{ ...td, minWidth: WIDTH[key], background: err ? "#fef2f2" : undefined }}>
        {control}
        {err && <span id={errId} style={errText}>{err}</span>}
      </td>
    );
  };

  return (
    <div>
      <p id={helpId} style={{ fontSize: 13, color: "#374151", margin: "0 0 10px", maxWidth: 820 }}>
        Edit the cells, or paste a block copied from a spreadsheet: it fills the cells from the one you are
        in to the right and down; pasted on a column name, its first column names the rows, and a first line
        of headers (Column, Type, Title, …) places the columns by header. Ctrl+arrow keys move between cells. Save sends every
        changed row at once and confirms it.
      </p>
      <div role="status" aria-live="polite" style={{ fontSize: 13, color: G_DARK, marginBottom: messages.length ? 8 : 0 }}>
        {messages.map(m => <div key={m}>{m}</div>)}
      </div>
      <ErrorNote error={error} />
      <div data-table-scroll style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 12 }}>
        <table ref={tableRef} role="grid" aria-label={`Dictionary grid of ${file.filename}`} aria-describedby={helpId}
               onKeyDown={onKeyDown} onPaste={onPaste}
               style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th scope="col" style={th}>Column</th>
              {GRID_FIELDS.map(f => <th key={f.key} scope="col" style={th}>{f.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {columns.map((col, r) => {
              const rowErr = (server[r] || {}).column;
              return (
                <tr key={col.id} style={{ background: patches[r] ? "#fffbeb" : undefined }}>
                  <th scope="row" data-cell={`${r}:0`} tabIndex={-1} data-invalid={rowErr ? "" : undefined}
                      style={{ ...td, textAlign: "left", fontWeight: 600, whiteSpace: "nowrap" }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{col.name}</span>
                    {patches[r] && <span style={{ display: "block", marginTop: 4 }}><Badge color="amber">changed</Badge></span>}
                    {rowErr && <span style={errText}>{rowErr}</span>}
                  </th>
                  {GRID_FIELDS.map(f => renderCell(r, f.key, col))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
        {tried && invalidCells > 0 && <span style={{ fontSize: 13, color: "#b91c1c" }}>{invalidCells} cell(s) to fix</span>}
        <Btn variant="secondary" onClick={discard} disabled={saving}>Discard changes</Btn>
        {onClose && <Btn variant="secondary" onClick={onClose} disabled={saving}>Back to the list</Btn>}
        <Btn onClick={save} disabled={saving}>
          {saving ? "Saving…" : `Save ${changed} changed column${changed === 1 ? "" : "s"}`}
        </Btn>
      </div>
    </div>
  );
}
